import React, { useEffect, useReducer } from "react";
import { useTranslation } from "react-i18next";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { makeApiCall } from "src/api/apiRequests";
import API_HEADERS from "src/api/apiConfig";
import API_PATHS from "src/api/apiPaths";
import i18n from "src/i18n/i18n";
import moment from "moment";
import "moment/locale/sq";
import "moment/locale/fr";
import FLASH_MESSAGES from "src/helpers/FlashMessages";

interface ShowBillProps {
  onShowModal: boolean;
  setShowModal: (visible: boolean) => void;
  invoiceId: number;
}

interface InvoiceDetails {
  loading: boolean;
  title: string;
  billNumber: string;
  month: string;
  year: string;
  nurseryKindergarten: string;
  guardianName: string;
  childName: string;
  email: string;
  phone: string;
  institutionName: string;
  institutionBranch: string;
  branchCode: string;
  presence: number;
  absence: number;
  discount: number;
  price: number;
  priceType: string;
  totalPrice: number;
}

const initialState: InvoiceDetails = {
  loading: true,
  title: "",
  billNumber: "",
  year: "",
  month: "",
  nurseryKindergarten: "",
  guardianName: "",
  childName: "",
  email: "",
  phone: "",
  institutionName: "",
  institutionBranch: "",
  branchCode: "",
  presence: 0,
  absence: 0,
  discount: 0,
  price: 0,
  priceType: "monthly",
  totalPrice: 0,
};

const reducer = (
  state: InvoiceDetails,
  action: Partial<InvoiceDetails>
): InvoiceDetails => ({
  ...state,
  ...action,
});

const ShowBill: React.FC<ShowBillProps> = ({
  onShowModal,
  setShowModal,
  invoiceId,
}) => {
  const { t } = useTranslation();
  const [invoiceDetails, dispatch] = useReducer(reducer, initialState);

  const fetchInvoiceDetails = async (): Promise<void> => {
    try {
      const response: any = await makeApiCall<ResponseType>(
        API_PATHS.childrenInvoiceDetails(invoiceId),
        "GET",
        API_HEADERS.authenticated
      );
      // console.log(response);
      const { invoice, institutionData, discountPercentage } = response;

    //  console.log(response);

      dispatch({
        loading: false,
        title: invoice.invoice_number,
        billNumber: invoice.invoice_number,
        nurseryKindergarten: invoice.kindergarten.name,
        guardianName: `${invoice.child.guardian.first_name} ${invoice.child.guardian.last_name}`,
        childName: `${invoice.child.first_name} ${invoice.child.last_name}`,
        email: invoice.child.guardian.email,
        phone: invoice.child.guardian.phone,
        institutionName: institutionData.name,
        institutionBranch: institutionData.code,
        branchCode: institutionData.branch_code,
        month: moment()
          .locale(i18n.language === "al" ? "sq" : "en")
          .month(invoice.month - 1)
          .format("MMMM"),
        year: invoice.year,
        presence: invoice.presence,
        absence: invoice.absence,
        discount: discountPercentage ? discountPercentage.percentage : 0,
        price: invoice.price,
        priceType: invoice.price_type,
        totalPrice: invoice.total,
      });
    } catch (error: any) {
      FLASH_MESSAGES.errorMsg(error.response.data.error);
      onClose();
    }
  };

  useEffect(() => {
    fetchInvoiceDetails();
  }, []);

  const onClose = () => {
    setShowModal(false);
  };

  return (
    <Modal
      isOpen={onShowModal && !invoiceDetails.loading}
      toggle={() => onClose()}
      size="lg"
      centered
    >
      <ModalHeader
        className="text-dark font-size-15"
        toggle={() => onClose()}
        tag="span"
      >
        {t("child_billing_show_title") + invoiceDetails.title}
      </ModalHeader>
      <ModalBody>
        <div className="modal-info-content">
          <div className="modal-info-block">
            <span className="title">
              {t("child_billing_show_institutional_data")}:
            </span>
            <div className="row">
              <div className="col-12 col-sm-6">
                <span className="label">
                  {t("child_billing_show_name_of_the_institution")}:
                </span>
                <span className="content">
                  {invoiceDetails.institutionName}
                </span>
              </div>
              <div className="col-12 col-sm-6">
                <span className="label">
                  {t("child_billing_show_branch_code")}:
                </span>
                <span className="content">{invoiceDetails.branchCode}</span>
              </div>
              <div className="col-12 col-sm-6">
                <span className="label">
                  {t("child_billing_show_institution_code")}:
                </span>
                <span className="content">
                  {invoiceDetails.institutionBranch}
                </span>
              </div>
            </div>
          </div>
          <div className="modal-info-block">
            <span className="title">
              {t("child_billing_show_bill_details")}:
            </span>
            <div className="row">
              <div className="col-12 col-sm-6">
                <span className="label">
                  {t("child_billing_show_bill_number")}:
                </span>
                <span className="content">{invoiceDetails.billNumber}</span>
              </div>
              <div className="col-12 col-sm-6">
                <span className="label">
                  {t("child_billing_show_bill_month")}:
                </span>
                <span className="content">{invoiceDetails.month}</span>
              </div>
              <div className="col-12 col-sm-6">
                <span className="label">
                  {t("child_billing_show_bill_year")}:
                </span>
                <span className="content">{invoiceDetails.year}</span>
              </div>

              <div className="col-12 col-sm-6">
                <span className="label">
                  {t("child_billing_show_bill_nursery_kindergarten")}:
                </span>
                <span className="content">
                  {invoiceDetails.nurseryKindergarten}
                </span>
              </div>
            </div>
          </div>
          <div className="modal-info-block">
            <span className="title">{t("child_billing_show_billed_to")}:</span>
            <div className="row">
              <div className="col-12 col-sm-6">
                <span className="label">
                  {t("child_billing_show_guardian_name")}:
                </span>
                <span className="content">{invoiceDetails.guardianName}</span>
              </div>
              <div className="col-12 col-sm-6">
                <span className="label">{t("child_billing_show_email")}:</span>
                <span className="content">
                  {invoiceDetails.email ? (
                    <a href={`mailto:${invoiceDetails.email}`}>
                      {invoiceDetails.email}
                    </a>
                  ) : (
                    "-"
                  )}
                </span>
              </div>
              <div className="col-12 col-sm-6">
                <span className="label">
                  {t("child_billing_show_child_name")}:
                </span>
                <span className="content">{invoiceDetails.childName}</span>
              </div>
              <div className="col-12 col-sm-6">
                <span className="label">{t("child_billing_show_phone")}:</span>
                <span className="content">
                  {invoiceDetails.phone ? (
                    <a href={`tel:${invoiceDetails.phone}`}>
                      {invoiceDetails.phone}
                    </a>
                  ) : (
                    "-"
                  )}
                </span>
              </div>
            </div>
          </div>

          <div className="row invoice">
            <div className="table-container">
              <div className="row header">
                <div className="cell">
                  {t("child_billing_show_description")}
                </div>
                <div className="cell text-end">
                  {t("child_billing_show_amount")}
                </div>
              </div>
              <div className="row">
                <div className="cell description">
                  {t("child_billing_show_days_attended")}
                </div>
                <div className="cell amount text-end">
                  {invoiceDetails.presence}
                </div>
              </div>
              <div className="row">
                <div className="cell description">
                  {t("child_billing_show_absences")}
                </div>
                <div className="cell amount text-end">
                  {invoiceDetails.absence}
                </div>
              </div>
              <div className="row">
                <div className="cell description">
                  {t("child_billing_show_price_" + invoiceDetails.priceType)}
                </div>
                <div className="cell amount text-end">
                  {Number(invoiceDetails.price).toFixed(0)}
                </div>
              </div>
              <div className="row">
                <div className="cell description">
                  {t("child_billing_show_discount_percentage")}
                </div>
                <div className="cell amount text-end">
                  {invoiceDetails.discount} %
                </div>
              </div>

              <div className="row total">
                <div className="tr">
                  <div className="cell description">
                    {t("child_billing_show_calculated_amount")}
                  </div>
                  <div className="cell amount text-end">
                    {Number(
                      invoiceDetails.priceType === "monthly"
                        ? invoiceDetails.price
                        : invoiceDetails.price * invoiceDetails.presence
                    ).toFixed(0)}{" "}
                    {t("child_billing_show_currency")}
                  </div>
                </div>
                <div className="tr">
                  <div className="cell description">
                    {t("child_billing_show_calculated_discount")}
                  </div>
                  <div className="cell amount text-end">
                    {invoiceDetails.priceType === "monthly"
                      ? invoiceDetails.price * (invoiceDetails.discount / 100)
                      : invoiceDetails.price *
                        invoiceDetails.presence *
                        (invoiceDetails.discount / 100)}{" "}
                    {t("child_billing_show_currency")}
                  </div>
                </div>
                <div className="total_amount">
                  <div className="cell description">
                    {t("child_billing_show_calculated_total")}
                  </div>
                  <div className="cell amount text-end">
                    {Number(invoiceDetails.totalPrice).toFixed(0)}{" "}
                    {t("child_billing_show_currency")}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <button
          className="button-white"
          type="button"
          onClick={() => onClose()}
        >
          {t("child_billing_show_close")}
        </button>
      </ModalFooter>
    </Modal>
  );
};

export default ShowBill;
